import PropTypes from 'prop-types'
import cx from 'classnames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Link from 'next/link'

import s from './styles.module.css'

/**
 * The Chip component can be used for a variety of applications
 * ranging from displaying data to as part of a filer or list control
 * component.
 */
const Chip = ({
  active = false,
  className,
  clickable = true,
  icon,
  label,
  onClick,
  to,
  value = undefined,
}) => {
  const mappedClassName = cx(className, s.root, {
    [s.active]: active,
    [s.disabled]: !clickable,
  })

  const handleClick = value => e => {
    if (!clickable) return
    onClick?.(value)
  }

  // A chip that does something on click is a toggle button, so keyboard
  // users can focus it and choose it with Enter or Space.
  const isButton = clickable && !!onClick
  const handleKeyDown = e => {
    if (e.key !== 'Enter' && e.key !== ' ') return
    e.preventDefault()
    // A held key repeats keydown; toggle once per press.
    if (e.repeat) return
    onClick(value)
  }

  // render the link variant
  if (to) {
    return (
      <Link href={to} className={mappedClassName} onClick={handleClick(value)}>
        {icon && <FontAwesomeIcon icon={icon} className={s.icon} />}
        <span title={label} className={s.label}>
          {' '}
          {label}
        </span>
      </Link>
    )
  }

  return (
    <div
      className={mappedClassName}
      onClick={handleClick(value)}
      {...(isButton && {
        role: 'button',
        tabIndex: 0,
        'aria-pressed': active,
        onKeyDown: handleKeyDown,
      })}>
      {icon && <FontAwesomeIcon icon={icon} className={s.icon} />}
      <span title={label} className={s.label}>
        {' '}
        {label}
      </span>
    </div>
  )
}

Chip.propTypes = {
  /**
   * Whether the chip should appear in an active state
   */
  active: PropTypes.bool,
  /**
   * CSS class name to apply to the chip root
   */
  className: PropTypes.string,
  /**
   * Whether the user should be able to click on the chip
   */
  clickable: PropTypes.bool,
  /**
   * Optional icon to display to the left of the label
   */
  icon: PropTypes.object,
  /**
   * Text that should be displayed
   */
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.element,
  ]).isRequired,
  /**
   * Optional event handler that fires when the user clicks on
   * the chip. The chip value is exposed as a function parameter.
   */
  onClick: PropTypes.func,
  /**
   * An optional property that allows the chip to function as a link
   */
  to: PropTypes.string,
  /**
   * Internal value associated with the chip, i.e. a database index
   */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

export default Chip
